/**
 * Google Workspace REST API Helpers for ARMELLINI CRM
 * Uses the user's Google OAuth Access Token obtained during Sign-In
 */

type TokenExpiredListener = (reason?: string) => void;
const tokenExpiredListeners: TokenExpiredListener[] = [];

export function onGoogleTokenExpired(listener: TokenExpiredListener) {
  tokenExpiredListeners.push(listener);
  return () => {
    const idx = tokenExpiredListeners.indexOf(listener);
    if (idx !== -1) tokenExpiredListeners.splice(idx, 1);
  };
}

export function notifyGoogleTokenExpired(reason?: string) {
  tokenExpiredListeners.forEach(fn => fn(reason));
}

/**
 * Checks whether the currently stored Google OAuth access token is missing or expired.
 * Google OAuth tokens expire in 60 minutes (3600 seconds). We consider it expired
 * if it is >= 55 minutes old or missing.
 */
export function isGoogleTokenExpired(): boolean {
  if (typeof window === 'undefined' || !window.localStorage) return true;
  const token = localStorage.getItem('google_access_token');
  const tsStr = localStorage.getItem('google_access_token_timestamp');
  if (!token || !tsStr) return true;
  const ts = parseInt(tsStr, 10);
  if (isNaN(ts)) return true;
  return Date.now() - ts >= 55 * 60 * 1000;
}

async function handleGoogleApiResponse<T>(response: Response, defaultErrorMsg: string): Promise<T> {
  if (!response.ok) {
    if (response.status === 401) {
      notifyGoogleTokenExpired(`HTTP ${response.status} from Google API`);
    }
    const err = await response.json().catch(() => ({}));
    const message = err.error?.message || defaultErrorMsg;
    const lower = message.toLowerCase();
    if (response.status === 401 && (lower.includes('unauthorized') || lower.includes('invalid credentials') || lower.includes('token expired') || lower.includes('invalid_grant'))) {
      notifyGoogleTokenExpired(message);
    }
    throw new Error(message);
  }
  return response.json();
}

// 1. GMAIL INTEGRATION - Send Emails
/**
 * Generates a unique tracking reference for an outbound CRM email.
 * The token rides in the subject line, so it survives into every reply
 * ("Re: ... [#AEL-XXXXXX]") and lets the CRM match replies back to a lead.
 */
export function generateTrackingId(): string {
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let id = '';
  for (let i = 0; i < 6; i++) id += alphabet[Math.floor(Math.random() * alphabet.length)];
  return `AEL-${id}`;
}

export async function sendGmailEmail(
  token: string,
  to: string,
  subject: string,
  body: string,
  trackingId?: string,
  isHtml?: boolean
): Promise<{ id: string; threadId: string }> {
  const taggedSubject = trackingId ? `${subject} [#${trackingId}]` : subject;
  const contentType = isHtml ? 'Content-Type: text/html; charset=utf-8' : 'Content-Type: text/plain; charset=utf-8';
  const emailStr = [
    `To: ${to}`,
    `Subject: ${taggedSubject}`,
    contentType,
    'MIME-Version: 1.0',
    ...(trackingId ? [`X-AEL-Tracking: ${trackingId}`] : []),
    '',
    body
  ].join('\n');


  // Base64Url encode the mime message
  const raw = btoa(unescape(encodeURIComponent(emailStr)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');

  const response = await fetch('https://gmail.googleapis.com/gmail/v1/users/me/messages/send', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ raw }),
  });

  return handleGoogleApiResponse<{ id: string; threadId: string }>(response, 'Failed to send Gmail message');
}

export interface GmailMessageMeta {
  id: string;
  threadId: string;
  from: string;
  subject: string;
  date: string;
  snippet: string;
}

async function fetchGmailMessageMeta(token: string, id: string): Promise<GmailMessageMeta> {
  const res = await fetch(
    `https://gmail.googleapis.com/gmail/v1/users/me/messages/${id}?format=metadata&metadataHeaders=From&metadataHeaders=Subject&metadataHeaders=Date`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  if (!res.ok) {
    if (res.status === 401) notifyGoogleTokenExpired(`HTTP ${res.status}`);
    throw new Error('Failed to fetch Gmail message');
  }
  const m = await res.json();
  const header = (name: string) =>
    m.payload?.headers?.find((h: any) => h.name.toLowerCase() === name.toLowerCase())?.value || '';
  return {
    id: m.id,
    threadId: m.threadId,
    from: header('From'),
    subject: header('Subject'),
    date: header('Date'),
    snippet: m.snippet || '',
  };
}

// List recent inbox messages (metadata only)
export async function listRecentGmailMessages(token: string, maxResults = 8): Promise<GmailMessageMeta[]> {
  const res = await fetch(
    `https://gmail.googleapis.com/gmail/v1/users/me/messages?maxResults=${maxResults}&labelIds=INBOX`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  if (!res.ok) {
    if (res.status === 401) notifyGoogleTokenExpired(`HTTP ${res.status}`);
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error?.message || 'Failed to list Gmail messages');
  }
  const data = await res.json();
  const ids: { id: string }[] = data.messages || [];
  return Promise.all(ids.map(m => fetchGmailMessageMeta(token, m.id)));
}

// Fetch all messages in a thread (used to find replies to tracked emails)
export async function fetchGmailThreadMessages(token: string, threadId: string): Promise<GmailMessageMeta[]> {
  const res = await fetch(
    `https://gmail.googleapis.com/gmail/v1/users/me/threads/${threadId}?format=metadata&metadataHeaders=From&metadataHeaders=Subject&metadataHeaders=Date`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  if (!res.ok) {
    if (res.status === 401) notifyGoogleTokenExpired(`HTTP ${res.status}`);
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error?.message || 'Failed to fetch Gmail thread');
  }
  const data = await res.json();
  return (data.messages || []).map((m: any) => {
    const header = (name: string) =>
      m.payload?.headers?.find((h: any) => h.name.toLowerCase() === name.toLowerCase())?.value || '';
    return {
      id: m.id,
      threadId: m.threadId,
      from: header('From'),
      subject: header('Subject'),
      date: header('Date'),
      snippet: m.snippet || '',
    };
  });
}

// 2. GOOGLE SHEETS INTEGRATION - Create Spreadsheets & Append Data
export async function createGoogleSheet(token: string, title: string): Promise<any> {
  const response = await fetch('https://sheets.googleapis.com/v4/spreadsheets', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      properties: { title },
    }),
  });

  return handleGoogleApiResponse(response, 'Failed to create Google Sheet');
}

export async function appendToGoogleSheet(
  token: string,
  spreadsheetId: string,
  range: string,
  values: any[][]
): Promise<any> {
  const url = `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values/${range}:append?valueInputOption=USER_ENTERED`;
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ values }),
  });

  return handleGoogleApiResponse(response, 'Failed to append data to Google Sheet');
}

// 3. GOOGLE CALENDAR INTEGRATION - Create Events
export async function createCalendarEvent(
  token: string,
  summary: string,
  description: string,
  startTime: string, // ISO String
  endTime: string // ISO String
): Promise<any> {
  const response = await fetch('https://www.googleapis.com/calendar/v3/calendars/primary/events', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      summary,
      description,
      start: { dateTime: startTime },
      end: { dateTime: endTime },
    }),
  });

  return handleGoogleApiResponse(response, 'Failed to schedule Google Calendar event');
}

export interface CalendarEvent {
  id: string;
  summary: string;
  start: string;
  end: string;
  htmlLink?: string;
}

// List upcoming events from the primary calendar
export async function listUpcomingCalendarEvents(token: string, maxResults = 8): Promise<CalendarEvent[]> {
  const timeMin = encodeURIComponent(new Date().toISOString());
  const res = await fetch(
    `https://www.googleapis.com/calendar/v3/calendars/primary/events?timeMin=${timeMin}&maxResults=${maxResults}&singleEvents=true&orderBy=startTime`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  if (!res.ok) {
    if (res.status === 401) notifyGoogleTokenExpired(`HTTP ${res.status}`);
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error?.message || 'Failed to list Calendar events');
  }
  const data = await res.json();
  return (data.items || []).map((e: any) => ({
    id: e.id,
    summary: e.summary || '(no title)',
    start: e.start?.dateTime || e.start?.date || '',
    end: e.end?.dateTime || e.end?.date || '',
    htmlLink: e.htmlLink,
  }));
}

// List spreadsheets on Drive (for the Sheets module)
export async function listSpreadsheets(token: string, pageSize = 10): Promise<DriveFile[]> {
  const q = encodeURIComponent("mimeType='application/vnd.google-apps.spreadsheet'");
  const res = await fetch(
    `https://www.googleapis.com/drive/v3/files?q=${q}&pageSize=${pageSize}&orderBy=modifiedTime desc&fields=files(id,name,mimeType,webViewLink,iconLink)`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  if (!res.ok) {
    if (res.status === 401) notifyGoogleTokenExpired(`HTTP ${res.status}`);
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error?.message || 'Failed to list spreadsheets');
  }
  const data = await res.json();
  return data.files || [];
}

export interface GoogleContact {
  name: string;
  email: string;
  phone: string;
  company: string;
}

// List the user's Google Contacts
export async function listGoogleContacts(token: string, pageSize = 15): Promise<GoogleContact[]> {
  const res = await fetch(
    `https://people.googleapis.com/v1/people/me/connections?personFields=names,emailAddresses,phoneNumbers,organizations&pageSize=${pageSize}&sortOrder=LAST_MODIFIED_DESCENDING`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  if (!res.ok) {
    if (res.status === 401) notifyGoogleTokenExpired(`HTTP ${res.status}`);
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error?.message || 'Failed to list Google Contacts');
  }
  const data = await res.json();
  return (data.connections || []).map((p: any) => ({
    name: p.names?.[0]?.displayName || '(no name)',
    email: p.emailAddresses?.[0]?.value || '',
    phone: p.phoneNumbers?.[0]?.value || '',
    company: p.organizations?.[0]?.name || '',
  }));
}

// Read a sheet tab as row objects keyed by (trimmed) header names
export async function fetchSheetRows(
  token: string,
  spreadsheetId: string,
  sheetName: string
): Promise<Record<string, string>[]> {
  const res = await fetch(
    `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values/${encodeURIComponent(sheetName)}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  if (!res.ok) {
    if (res.status === 401) notifyGoogleTokenExpired(`HTTP ${res.status}`);
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error?.message || `Failed to read sheet "${sheetName}"`);
  }
  const data = await res.json();
  const values: string[][] = data.values || [];
  if (values.length < 2) return [];
  const headers = values[0].map(h => String(h || '').trim());
  return values.slice(1).map(row => {
    const obj: Record<string, string> = {};
    headers.forEach((h, i) => { if (h) obj[h] = row[i] !== undefined ? String(row[i]) : ''; });
    return obj;
  });
}

// GOOGLE WORKSPACE ADMIN DIRECTORY - list domain users (requires a
// Workspace admin account; used to import teammates into the CRM)
export interface WorkspaceUser {
  email: string;
  name: string;
  isAdmin: boolean;
  suspended: boolean;
  photoUrl?: string;
}

export async function listWorkspaceUsers(token: string, maxResults = 200): Promise<WorkspaceUser[]> {
  const res = await fetch(
    `https://admin.googleapis.com/admin/directory/v1/users?customer=my_customer&maxResults=${maxResults}&orderBy=email`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  if (!res.ok) {
    if (res.status === 401) notifyGoogleTokenExpired(`HTTP ${res.status}`);
    const err = await res.json().catch(() => ({}));
    const msg = err.error?.message || 'Failed to list Workspace users';
    if (res.status === 403) {
      throw new Error(`${msg} — listing the directory requires a Google Workspace admin account (and a fresh sign-in to grant the directory permission).`);
    }
    throw new Error(msg);
  }
  const data = await res.json();
  return (data.users || []).map((u: any) => ({
    email: u.primaryEmail,
    name: u.name?.fullName || u.primaryEmail,
    isAdmin: !!u.isAdmin,
    suspended: !!u.suspended,
    photoUrl: u.thumbnailPhotoUrl,
  }));
}

// 4. GOOGLE DRIVE INTEGRATION - List files (Drive Browser & Picker)
export interface DriveFile {
  id: string;
  name: string;
  mimeType: string;
  webViewLink?: string;
  iconLink?: string;
}

export async function listDriveFiles(token: string, pageSize = 15): Promise<DriveFile[]> {
  const response = await fetch(
    `https://www.googleapis.com/drive/v3/files?pageSize=${pageSize}&fields=files(id,name,mimeType,webViewLink,iconLink)`,
    {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (!response.ok) {
    if (response.status === 401) notifyGoogleTokenExpired(`HTTP ${response.status}`);
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error?.message || 'Failed to search Google Drive files');
  }

  const data = await response.json();
  return data.files || [];
}

// 5. GOOGLE CONTACTS (People API) - Create Contact Cards
export async function createGoogleContact(
  token: string,
  firstName: string,
  lastName: string,
  company: string,
  email: string,
  phone: string
): Promise<any> {
  const body = {
    names: [{ givenName: firstName, familyName: lastName }],
    organizations: [{ name: company, title: 'Lead Client' }],
    emailAddresses: email ? [{ value: email, type: 'work' }] : [],
    phoneNumbers: phone ? [{ value: phone, type: 'work' }] : [],
  };

  const response = await fetch('https://people.googleapis.com/v1/people:createContact', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });

  return handleGoogleApiResponse(response, 'Failed to sync contact to Google Contacts');
}

// 6. GOOGLE CHAT INTEGRATION - Send messages to a Space
export interface ChatSpace {
  name: string; // "spaces/..."
  displayName: string;
  type: string;
}

export async function listChatSpaces(token: string): Promise<ChatSpace[]> {
  const response = await fetch('https://chat.googleapis.com/v1/spaces', {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    if (response.status === 401) notifyGoogleTokenExpired(`HTTP ${response.status}`);
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error?.message || 'Failed to fetch Google Chat Spaces');
  }

  const data = await response.json();
  return data.spaces || [];
}

export async function sendChatMessage(
  token: string,
  spaceName: string, // "spaces/<id>"
  text: string
): Promise<any> {
  const url = `https://chat.googleapis.com/v1/${spaceName}/messages`;
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ text }),
  });

  return handleGoogleApiResponse(response, 'Failed to post Google Chat message');
}
